import type { BoardData } from '../model/board';
import { BoardIndex } from './boardIndex';

export interface SolutionValidation {
  readonly valid: boolean;
  readonly appliedMoves: number;
  readonly remainingArrows: number;
  readonly failureIndex?: number;
  readonly failureReason?: string;
}

/**
 * Replays a candidate solution against the rules and reports precisely where it breaks.
 * Every generated level is validated with this before it is ever handed to a player.
 */
export function validateSolution(
  board: BoardData,
  solution: readonly number[],
): SolutionValidation {
  const index = BoardIndex.fromBoard(board);
  const seen = new Set<number>();

  for (let i = 0; i < solution.length; i += 1) {
    const id = solution[i];
    if (!Number.isInteger(id) || id < 0 || id >= board.arrows.length) {
      return failure(i, 'unknown-arrow-id', index);
    }
    if (seen.has(id)) return failure(i, 'duplicate-arrow', index);
    if (!index.isAlive(id)) return failure(i, 'already-removed', index);
    if (!index.isRemovable(id)) return failure(i, 'blocked-arrow', index);
    seen.add(id);
    index.remove(id);
  }

  if (!index.isCleared) {
    return {
      valid: false,
      appliedMoves: solution.length,
      remainingArrows: index.aliveCount,
      failureReason: 'board-not-cleared',
    };
  }
  return { valid: true, appliedMoves: solution.length, remainingArrows: 0 };
}

function failure(atIndex: number, reason: string, index: BoardIndex): SolutionValidation {
  return {
    valid: false,
    appliedMoves: atIndex,
    remainingArrows: index.aliveCount,
    failureIndex: atIndex,
    failureReason: reason,
  };
}

/** Applies removals to a fresh index, ignoring the outcome. Used to rebuild saved sessions. */
export function applyRemovals(
  board: BoardData,
  removedIds: readonly number[],
): { index: BoardIndex; applied: number[]; rejected: number[] } {
  const index = BoardIndex.fromBoard(board);
  const applied: number[] = [];
  const rejected: number[] = [];
  for (const id of removedIds) {
    if (
      Number.isInteger(id) &&
      id >= 0 &&
      id < board.arrows.length &&
      index.isAlive(id) &&
      index.isRemovable(id)
    ) {
      index.remove(id);
      applied.push(id);
    } else {
      rejected.push(id);
    }
  }
  return { index, applied, rejected };
}
